import React, {useState, useEffect} from 'react';
import {Action, view} from '@sail/ui';
import {SearchItem} from 'src/v2/components/walletSelect/SearchItem';
import {WalletGrid} from 'src/v2/components/walletSelect/WalletGrid';
import {defineMessages, useIntl} from 'react-intl';
import {ChipButton} from 'src/v2/components/common';
import {setValue, useSelector} from 'src/v2/state/store';
import {wallet, qrCode} from '@sail/icons/react/Icon';
import type {PaymentOption} from 'src/v2/state/types';

interface Props {
  walletOptions: PaymentOption[];
  onSearchPress: () => void;
}

const messages = defineMessages({
  back: {
    id: 'crypto.payins.v2.wallet_select_grid.back',
    description: 'Button text to go back to the connected wallets screen',
    defaultMessage: 'Use connected wallets',
  },
  directDeposit: {
    id: 'crypto.payins.v2.wallet_select.direct_deposit',
    description:
      'Label for the direct deposit via QR code option in the wallet select grid',
    defaultMessage: 'Direct deposit via QR code',
  },
});

const ALLOWED_WALLETS = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'rainbow',
  'trust',
  'okxwallet',
  'ledger',
];

// TODO (stellacai): remove preferred placement for Crypto.com at end of Q1 2026
const ALLOWED_WALLETS_WITH_EXCHANGE = [
  'Coinbase Wallet',
  'Phantom',
  'MetaMask',
  'cryptoDotCom',
  'trust',
  'okxwallet',
  'ledger',
];

export const WalletSelectGrid = ({walletOptions, onSearchPress}: Props) => {
  const intl = useIntl();
  const exchangeFeatureFlagEnabled = useSelector(
    (state) => state.featureFlags.enable_crypto_payins_pay_from_exchange,
  );
  const directDepositEnabled = useSelector(
    (state) =>
      state.featureFlags.enable_crypto_payins_direct_deposit &&
      state.settlementPartner === 'bridge' &&
      !state.setupFutureUsage,
  );

  const setupFutureUsage = useSelector((state) => state.setupFutureUsage);
  const exchangePaymentsEnabled =
    !setupFutureUsage && exchangeFeatureFlagEnabled;

  const defaultWallets = exchangePaymentsEnabled
    ? ALLOWED_WALLETS_WITH_EXCHANGE
    : ALLOWED_WALLETS;

  const filteredWalletOptions = walletOptions
    .filter((walletOption) => {
      const walletKey =
        walletOption.provider === 'WalletConnect' ||
        walletOption.provider === 'Exchange'
          ? walletOption.key
          : walletOption.provider;
      return defaultWallets.includes(walletKey);
    })
    .sort((a, b) => {
      const getWalletKey = (walletOption: PaymentOption) =>
        walletOption.provider === 'WalletConnect' ||
        walletOption.provider === 'Exchange'
          ? walletOption.key
          : walletOption.provider;

      const indexA = defaultWallets.indexOf(getWalletKey(a));
      const indexB = defaultWallets.indexOf(getWalletKey(b));

      return indexA - indexB;
    });

  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const [contentVisible, setContentVisible] = useState(false);

  const showBackButton = connectedWallets.length > 0;

  const handleDirectDepositClick = () => {
    setValue((state) => {
      state.screen = 'DIRECT_DEPOSIT';
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setContentVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const onBackPress = () => {
    setValue((state) => {
      state.screen = 'WALLET_SWITCH';
    });
  };

  return (
    <view.div
      css={{
        stack: 'y',
        gapY: 'space.150',
        alignX: 'center',
        opacity: contentVisible ? 1 : 0,
        transition: 'opacity 100ms ease-in-out',
      }}
    >
      <view.div
        css={{
          stack: 'x',
          width: 'fill',
          minTileWidth: '76px',
          minTileHeight: '104px',
          alignX: 'center',
          alignY: 'top',
          gapY: 'space.150',
        }}
      >
        <Action onPress={onSearchPress}>
          <SearchItem />
        </Action>
        <WalletGrid walletOptions={filteredWalletOptions} />
      </view.div>
      {directDepositEnabled ? (
        <ChipButton
          icon={qrCode}
          onClick={handleDirectDepositClick}
          label={intl.formatMessage(messages.directDeposit)}
        />
      ) : null}
      {showBackButton ? (
        <ChipButton
          icon={wallet}
          onClick={onBackPress}
          label={intl.formatMessage(messages.back)}
        />
      ) : null}
    </view.div>
  );
};
